import type { Fleet } from '@ottd/api';
import { useTranslation } from 'react-i18next';

import { Hint } from '@/components/prototype/hint';
import { formatMoney, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

/*
 * Fleet display for game/company rows: balance + train/road/ship/aircraft
 * counts with the prototype's pixel icons. `road` = vehicles, `aircraft` =
 * airplanes (audit G9). `variant`: `chips` (boxed, labelled) or `inline`
 * (compact icon+value stacks).
 */

const ICONS = {
  balance: '/icons/icon-balance.png',
  trains: '/icons/icon-train.png',
  road: '/icons/icon-vehicle.png',
  ships: '/icons/icon-ship.png',
  aircraft: '/icons/icon-airplane.png',
} as const;

interface FleetChipsProps {
  fleet: Fleet;
  variant?: 'chips' | 'inline';
  className?: string;
}

export function FleetChips({ fleet, variant = 'inline', className }: FleetChipsProps) {
  const { t } = useTranslation('common');

  const items: Array<{ key: keyof typeof ICONS; label: string; value: string }> = [
    { key: 'balance', label: t('fleet.balance'), value: formatMoney(fleet.balance) },
    { key: 'trains', label: t('fleet.trains'), value: formatNumber(fleet.trains) },
    { key: 'road', label: t('fleet.road'), value: formatNumber(fleet.road) },
    { key: 'ships', label: t('fleet.ships'), value: formatNumber(fleet.ships) },
    { key: 'aircraft', label: t('fleet.aircraft'), value: formatNumber(fleet.aircraft) },
  ];

  if (variant === 'chips') {
    return (
      <div className={cn('flex flex-wrap gap-1.5', className)}>
        {items.map((item) => (
          <div key={item.key} className="bevel bg-panel-in flex items-center gap-2 px-2.5 py-1.5">
            <img
              src={ICONS[item.key]}
              alt=""
              width={20}
              height={16}
              style={{ imageRendering: 'pixelated' }}
            />
            <span className="font-mono text-[13px] text-text">{item.value}</span>
            <span className="text-faint text-[10px] uppercase">{item.label}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      {items.map((item) => (
        <Hint key={item.key} label={item.label}>
          <div className="flex items-center gap-1.5">
            <img
              src={ICONS[item.key]}
              alt={item.label}
              width={18}
              height={14}
              style={{ imageRendering: 'pixelated' }}
            />
            <span className="font-mono text-xs text-text">{item.value}</span>
          </div>
        </Hint>
      ))}
    </div>
  );
}
